import { useEffect, useMemo, useRef, useState } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import { Link } from 'react-router-dom';
import Header from '../../shared/layout/Header';
import developersApi from '../../api/services/developerService';
import ProjectVisualization from '../../features/projectVisualization/ProjectVisualization';
import StepIndicator from '../AgentApplication/components/StepIndicator';
import ProjectDocumentsStep from './projectWizard/ProjectDocumentsStep';
import ProjectGeospatialStep from './projectWizard/ProjectGeospatialStep';
import ProjectInfoStep from './projectWizard/ProjectInfoStep';
import ProjectSalesStep from './projectWizard/ProjectSalesStep';
import ProjectScheduleStep, { buildActivityPlan } from './projectWizard/ProjectScheduleStep';
import ProjectStructureStep from './projectWizard/ProjectStructureStep';
import { Select } from './projectWizard/WizardControls';
import './DevelopersPanel.css';

const emptyProject = {
  name: '',
  address: '',
  description: '',
  construction_started_at: '',
  expected_completion_at: '',
  total_units: '',
  latitude: '',
  longitude: ''
};

const defaultInstallments = [
  { title: 'پیش پرداخت', sequence: 1, amount_type: 'PERCENTAGE', percentage: '30', fixed_amount: '', trigger: 'contract_signing', due_date: '', offset_value: '', milestone: '' },
  { title: 'میان دوره ساخت', sequence: 2, amount_type: 'PERCENTAGE', percentage: '50', fixed_amount: '', trigger: 'construction_milestone', due_date: '', offset_value: '', milestone: 'اتمام اسکلت' },
  { title: 'تحویل', sequence: 3, amount_type: 'PERCENTAGE', percentage: '20', fixed_amount: '', trigger: 'handover', due_date: '', offset_value: '', milestone: '' }
];

const emptyScheduleForm = {
  reason: '',
  effective_from: '',
  starting_activity: 'SITE_PREPARATION',
  section: '',
  activities: []
};

const statusLabels = {
  UNVERIFIED: 'تایید نشده',
  UNDER_REVIEW: 'در حال بررسی',
  ADD_INFO: 'نیازمند اطلاعات',
  VERIFIED: 'تایید شده',
  REJECTED: 'رد شده',
  SUSPENDED: 'تعلیق شده',
  DRAFT: 'پیش نویس',
  PLANNING: 'برنامه ریزی',
  CONSTRUCTION: 'در حال ساخت',
  COMPLETED: 'تکمیل شده',
  CANCELLED: 'لغو شده',
  ACTIVE: 'فعال',
  PUBLISHED: 'منتشر شده',
  AVAILABLE: 'قابل عرضه',
  SOLD: 'فروخته شده',
  SOLD_OUT: 'فروخته شده',
  draft: 'پیش نویس',
  active: 'فعال',
  superseded: 'جایگزین شده'
};

const tabs = [
  { id: 'overview', label: 'نمای کلی' },
  { id: 'projects', label: 'ثبت و ویرایش پروژه' }
];

const projectWizardSteps = [
  'اطلاعات پروژه',
  'تحلیل مکانی',
  'مدارک پروژه',
  'ساختار، تیپ و واحدها',
  'فروش و آگهی',
  'برنامه ساخت'
];

const PROJECT_VERIFICATION_MESSAGE = 'برای ثبت پروژه جدید، پروفایل سازنده باید ابتدا توسط مدیر سیستم تایید شود. وضعیت درخواست خود را از بخش «پروفایل و تایید سازنده» پیگیری کنید.';

function normalizeError(error) {
  const data = error?.response?.data;
  if (!data) return 'خطا در ارتباط با سرور';
  if (typeof data === 'string') return data;
  if (data.detail) return data.detail;
  if (Array.isArray(data)) return data.join('، ');
  return Object.entries(data)
    .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join('، ') : JSON.stringify(value)}`)
    .join(' | ');
}

function Badge({ value, tone = 'neutral' }) {
  return <span className={`dev-badge ${tone}`}>{statusLabels[value] || value || 'نامشخص'}</span>;
}

function WizardChecklist({ status }) {
  const entries = Object.entries(status?.steps || {});
  if (!entries.length) return <p className="dev-muted">هنوز چک لیست ثبت پروژه دریافت نشده است.</p>;
  return (
    <div className="dev-checklist">
      {entries.map(([key, value]) => (
        <div className="dev-check" key={key}>
          <span className={value.complete ? 'done' : 'todo'}>{value.complete ? '✓' : '•'}</span>
          <strong>{stepLabel(key)}</strong>
          <small>{stepHint(value)}</small>
        </div>
      ))}
    </div>
  );
}

function stepLabel(key) {
  const labels = {
    project_information: 'اطلاعات پروژه',
    geo_features: 'تحلیل مکانی',
    documents: 'مدارک',
    unit_templates: 'تیپ واحدها',
    units: 'واحدها',
    sales_strategy_templates: 'الگوهای فروش',
    unit_sales_strategies: 'شرایط فروش واحدها',
    presale_listings: 'آگهی های پیش فروش',
    construction_schedule: 'برنامه ساخت'
  };
  return labels[key] || key;
}

function stepHint(value) {
  if (value.missing_fields?.length) return `فیلدهای ناقص: ${value.missing_fields.join(', ')}`;
  if (typeof value.count === 'number') return `${value.count} مورد`;
  if (typeof value.template_count === 'number') return `${value.template_count} تیپ، ${value.published_revision_count} نسخه منتشر شده`;
  return value.complete ? 'تکمیل شده' : 'در انتظار تکمیل';
}

export default function DevelopersPanel() {
  const [activeTab, setActiveTab] = useState('overview');
  const [projectStep, setProjectStep] = useState(1);
  const [firms, setFirms] = useState([]);
  const [selectedFirmId, setSelectedFirmId] = useState('');
  const [platformProjects, setPlatformProjects] = useState([]);
  const [portfolioProjects, setPortfolioProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [projectBundle, setProjectBundle] = useState({ documents: [], sections: [], templates: [], units: [], strategies: [], salesTemplates: [], schedules: [], activities: [], activityCatalog: [] });
  const [wizardStatus, setWizardStatus] = useState(null);
  const [projectForm, setProjectForm] = useState(emptyProject);
  const [templateForm, setTemplateForm] = useState({ name: '', area: '' });
  const [unitBatch, setUnitBatch] = useState({ template_revision: '', floor: '', section: '', count: '', unit_numbers: '' });
  const [salesForm, setSalesForm] = useState({ name: '', description: '', reservation_fee: '', price: '' });
  const [salesInstallments, setSalesInstallments] = useState(defaultInstallments);
  const [selectedSalesUnitIds, setSelectedSalesUnitIds] = useState([]);
  const [scheduleForm, setScheduleForm] = useState(emptyScheduleForm);
  const [editingScheduleVersionId, setEditingScheduleVersionId] = useState('');
  const [sectionForm, setSectionForm] = useState({ name: '', type: 'BUILDING', parent: '' });
  const [busy, setBusy] = useState(false);
  const [projectNavBusy, setProjectNavBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const geoStepRef = useRef(null);

  const selectedFirm = useMemo(
    () => firms.find((firm) => firm.id === selectedFirmId) || firms[0],
    [firms, selectedFirmId]
  );
  const selectedProject = useMemo(
    () => platformProjects.find((project) => project.id === selectedProjectId),
    [platformProjects, selectedProjectId]
  );
  const canRegisterNewProject = selectedFirm?.verification_status === 'VERIFIED';
  const activeScheduleVersion = useMemo(
    () => projectBundle.schedules.find((version) => version.status === 'active')
      || [...projectBundle.schedules].sort((left, right) => right.number - left.number)[0],
    [projectBundle.schedules]
  );
  const presaleReadyUnitCount = useMemo(() => {
    const strategiesByUnit = new Map(projectBundle.strategies.map((strategy) => [String(strategy.unit), strategy]));
    return projectBundle.units.filter((unit) => {
      const strategy = strategiesByUnit.get(String(unit.id));
      return strategy && Array.isArray(strategy.installments) && strategy.installments.length > 0;
    }).length;
  }, [projectBundle.strategies, projectBundle.units]);
  const dashboardProgress = useMemo(() => {
    const steps = Object.values(wizardStatus?.steps || {});
    if (!steps.length) return 0;
    return Math.round((steps.filter((step) => step.complete).length / steps.length) * 100);
  }, [wizardStatus]);
  const nextIncompleteStep = useMemo(() => {
    const stepMap = [
      ['project_information', 1],
      ['geo_features', 2],
      ['documents', 3],
      ['unit_templates', 4],
      ['units', 4],
      ['sales_strategy_templates', 5],
      ['unit_sales_strategies', 5],
      ['presale_listings', 5],
      ['construction_schedule', 6]
    ];
    return stepMap.find(([key]) => wizardStatus?.steps?.[key] && !wizardStatus.steps[key].complete)?.[1] || 1;
  }, [wizardStatus]);

  useEffect(() => {
    loadInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedFirm?.id) {
      loadFirmData(selectedFirm.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFirm?.id]);

  useEffect(() => {
    if (selectedProject?.id) {
      setSelectedProjectId(selectedProject.id);
      loadProjectBundle(selectedProject.id);
      setProjectForm({
        name: selectedProject.name || '',
        address: selectedProject.address || '',
        description: selectedProject.description || '',
        construction_started_at: selectedProject.construction_started_at || '',
        expected_completion_at: selectedProject.expected_completion_at || '',
        total_units: selectedProject.total_units || '',
        longitude: selectedProject.location?.coordinates?.[0] || '',
        latitude: selectedProject.location?.coordinates?.[1] || ''
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject?.id]);

  useEffect(() => {
    setSelectedSalesUnitIds([]);
    setScheduleForm(emptyScheduleForm);
    setEditingScheduleVersionId('');
  }, [selectedProjectId]);

  const handleProjectSelect = (projectId) => {
    if (!projectId && !canRegisterNewProject) {
      setNotice('');
      setError(PROJECT_VERIFICATION_MESSAGE);
      return;
    }
    setSelectedProjectId(projectId);
    if (!projectId) {
      setProjectForm(emptyProject);
      setWizardStatus(null);
      setProjectBundle({ documents: [], sections: [], templates: [], units: [], strategies: [], salesTemplates: [], schedules: [], activities: [], activityCatalog: [] });
      setScheduleForm(emptyScheduleForm);
      setEditingScheduleVersionId('');
    }
  };

  const openProjectStep = (step) => {
    if (!selectedProject && !canRegisterNewProject) {
      setNotice('');
      setError(PROJECT_VERIFICATION_MESSAGE);
      return;
    }
    setProjectStep(step);
    setActiveTab('projects');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const startNewProject = () => {
    if (!canRegisterNewProject) {
      setNotice('');
      setError(PROJECT_VERIFICATION_MESSAGE);
      return;
    }
    handleProjectSelect('');
    setProjectStep(1);
    setActiveTab('projects');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openTab = (tabId) => {
    if (tabId === 'projects' && !selectedProject && !canRegisterNewProject) {
      setNotice('');
      setError(PROJECT_VERIFICATION_MESSAGE);
      return;
    }
    setActiveTab(tabId);
  };

  const run = async (task, message) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await task();
      if (message) setNotice(message);
      return result;
    } catch (err) {
      setError(normalizeError(err));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const loadInitial = async () => {
    await run(async () => {
      const firmList = await developersApi.listFirms();
      setFirms(firmList);
      if (firmList[0]) setSelectedFirmId(firmList[0].id);
    });
  };

  const loadFirmData = async (developerId, preferredProjectId = '') => {
    await run(async () => {
      const [projects, portfolio] = await Promise.all([
        developersApi.listPlatformProjects(developerId),
        developersApi.listPortfolioProjects(developerId)
      ]);
      setPlatformProjects(projects);
      setPortfolioProjects(portfolio);
      setSelectedProjectId((current) => {
        if (preferredProjectId && projects.some((project) => project.id === preferredProjectId)) {
          return preferredProjectId;
        }
        if (current && projects.some((project) => project.id === current)) {
          return current;
        }
        return projects[0]?.id || '';
      });
    });
  };

  const loadProjectBundle = async (projectId) => {
    await run(async () => {
      const [status, documents, sections, templates, units, salesTemplates, strategies, schedules, activities, activityCatalog] = await Promise.all([
        developersApi.getWizardStatus(projectId).catch(() => null),
        developersApi.listPlatformDocuments(projectId),
        developersApi.listSections(projectId),
        developersApi.listUnitTemplates(projectId),
        developersApi.listUnits(projectId),
        developersApi.listSalesTemplates(projectId),
        developersApi.listUnitSalesStrategies(projectId),
        developersApi.listScheduleVersions(projectId),
        developersApi.listActivities(projectId),
        developersApi.listConstructionActivityCatalog()
      ]);
      setWizardStatus(status);
      setProjectBundle({ documents, sections, templates, units, salesTemplates, strategies, schedules, activities, activityCatalog });
    });
  };

  const savePlatformProject = async (event) => {
    event?.preventDefault?.();
    if (!selectedFirm?.id) return null;
    if (!selectedProject?.id && !canRegisterNewProject) {
      setNotice('');
      setError(PROJECT_VERIFICATION_MESSAGE);
      return null;
    }
    const payload = {
      developer: selectedFirm.id,
      name: projectForm.name,
      address: projectForm.address,
      description: projectForm.description,
      construction_started_at: projectForm.construction_started_at || null,
      expected_completion_at: projectForm.expected_completion_at || null,
      total_units: projectForm.total_units || null,
      location: projectForm.latitude && projectForm.longitude
        ? { type: 'Point', coordinates: [Number(projectForm.longitude), Number(projectForm.latitude)] }
        : null
    };
    const saved = await run(
      () => selectedProject?.id ? developersApi.updatePlatformProject(selectedProject.id, payload) : developersApi.createPlatformProject(payload),
      'پروژه پیش فروش ذخیره شد.'
    );
    if (saved) {
      setPlatformProjects((current) => {
        const exists = current.some((project) => project.id === saved.id);
        return exists
          ? current.map((project) => project.id === saved.id ? saved : project)
          : [saved, ...current];
      });
      setSelectedProjectId(saved.id);
      setProjectForm({
        name: saved.name || '',
        address: saved.address || '',
        description: saved.description || '',
        construction_started_at: saved.construction_started_at || '',
        expected_completion_at: saved.expected_completion_at || '',
        total_units: saved.total_units || '',
        longitude: saved.location?.coordinates?.[0] || '',
        latitude: saved.location?.coordinates?.[1] || ''
      });
      await loadFirmData(selectedFirm.id, saved.id);
      setSelectedProjectId(saved.id);
    }
    return saved;
  };

  const uploadProjectDocument = async (formData) => {
    const uploaded = await run(
      () => developersApi.uploadPlatformDocument(selectedProject.id, formData),
      'مدرک پروژه بارگذاری شد.'
    );
    await loadProjectBundle(selectedProject.id);
    return uploaded;
  };

  const createSection = async (event) => {
    event.preventDefault();
    await run(() => developersApi.createSection({ ...sectionForm, project: selectedProject.id, parent: sectionForm.parent || null }), 'بخش پروژه اضافه شد.');
    setSectionForm({ name: '', type: 'BUILDING', parent: '' });
    await loadProjectBundle(selectedProject.id);
  };

  const deleteSection = async (sectionId) => {
    await run(() => developersApi.deleteSection(sectionId), 'ساختار پروژه حذف شد.');
    await loadProjectBundle(selectedProject.id);
  };

  const createTemplate = async (event, amenities = []) => {
    event.preventDefault();
    const name = templateForm.name.trim();
    const area = Number(templateForm.area);
    if (!name || !Number.isFinite(area) || area <= 0) {
      setError('نام تیپ و مساحت معتبر الزامی است.');
      setNotice('');
      return null;
    }
    const payload = {
      project: selectedProject.id,
      name,
      initial_revision: {
        area,
        amenities
      }
    };
    const template = await run(() => developersApi.createUnitTemplate(payload), 'تیپ واحد ایجاد شد.');
    if (template) setTemplateForm({ name: '', area: '' });
    await loadProjectBundle(selectedProject.id);
    return template;
  };

  const publishRevision = async (revisionId) => {
    await run(() => developersApi.publishUnitTemplateRevision(revisionId), 'نسخه تیپ واحد منتشر شد.');
    await loadProjectBundle(selectedProject.id);
  };

  const deleteUnitTemplate = async (templateId) => {
    await run(() => developersApi.deleteUnitTemplate(templateId), 'تیپ واحد حذف شد.');
    await loadProjectBundle(selectedProject.id);
  };

  const uploadUnitTemplatePlan = async (revisionId, formData) => {
    await run(() => developersApi.uploadUnitTemplatePlan(revisionId, formData), 'پلان تیپ بارگذاری شد.');
    await loadProjectBundle(selectedProject.id);
  };

  const bulkCreateUnits = async (event) => {
    event.preventDefault();
    const units = unitBatch.unit_numbers
      .split(/[\n,،]+/)
      .map((item) => item.trim())
      .filter(Boolean)
      .map((unit_number) => ({
        unit_number,
        floor: unitBatch.floor || null,
        section: unitBatch.section || null,
        status: 'PLANNED'
      }));
    await run(() => developersApi.bulkCreateUnits(selectedProject.id, { template_revision: unitBatch.template_revision, units }), 'واحدها ایجاد شدند.');
    setUnitBatch({ template_revision: '', floor: '', section: '', count: '', unit_numbers: '' });
    await loadProjectBundle(selectedProject.id);
  };

  const deleteUnit = async (unitId) => {
    await run(() => developersApi.deleteUnit(unitId), 'واحد حذف شد.');
    await loadProjectBundle(selectedProject.id);
  };

  const createSalesTemplate = async (event, installmentRows = salesInstallments) => {
    event.preventDefault();
    const installments = installmentRows.map((item, index) => ({
      ...item,
      sequence: index + 1,
      percentage: item.amount_type === 'PERCENTAGE' ? item.percentage : null,
      fixed_amount: item.amount_type === 'FIXED' ? item.fixed_amount : null,
      due_date: item.due_date || null,
      offset_value: item.offset_value ? Number(item.offset_value) : null
    }));
    const invalidInstallment = installments.find((item) => (
      !item.title.trim() ||
      (item.amount_type === 'PERCENTAGE' && (item.percentage === '' || item.percentage == null)) ||
      (item.amount_type === 'FIXED' && (item.fixed_amount === '' || item.fixed_amount == null)) ||
      (item.trigger === 'fixed_date' && !item.due_date) ||
      (['days_after_contract', 'months_after_contract'].includes(item.trigger) && (item.offset_value === '' || item.offset_value == null)) ||
      (item.trigger === 'construction_milestone' && !item.milestone.trim())
    ));
    if (!installments.length || invalidInstallment) {
      setError('عنوان، مبلغ و اطلاعات زمان‌بندی هر مرحله پرداخت را کامل کنید.');
      return;
    }
    await run(() => developersApi.createSalesTemplate({
      project: selectedProject.id,
      name: salesForm.name,
      description: salesForm.description,
      reservation_fee: salesForm.reservation_fee || null,
      installments
    }), 'الگوی فروش ساخته شد.');
    setSalesForm((prev) => ({ ...prev, name: '', description: '', reservation_fee: '' }));
    await loadProjectBundle(selectedProject.id);
  };

  const applySalesTemplate = async (templateId, unitIds = []) => {
    if (!unitIds.length || !salesForm.price) {
      setError('برای اعمال الگوی فروش، حداقل یک واحد انتخاب شده و قیمت پایه لازم است.');
      return;
    }
    await run(() => developersApi.applySalesTemplate(selectedProject.id, {
      template: templateId,
      unit_ids: unitIds,
      price_per_meter: salesForm.price
    }), 'شرایط فروش روی واحدها اعمال شد.');
    await loadProjectBundle(selectedProject.id);
  };

  const deleteSalesTemplate = async (templateId) => {
    if (!window.confirm('این الگوی فروش حذف شود؟ شرایط فروش واحدها باقی می‌ماند.')) return;
    await run(() => developersApi.deleteSalesTemplate(templateId), 'الگوی فروش حذف شد.');
    await loadProjectBundle(selectedProject.id);
  };

  const deleteUnitSalesStrategy = async (strategyId) => {
    if (!window.confirm('شرایط فروش این واحد حذف شود؟')) return;
    await run(() => developersApi.deleteUnitSalesStrategy(strategyId), 'شرایط فروش واحد حذف شد.');
    await loadProjectBundle(selectedProject.id);
  };

  const createListings = async () => {
    const strategiesByUnit = new Map(projectBundle.strategies.map((strategy) => [String(strategy.unit), strategy]));
    const eligibleUnits = projectBundle.units.filter((unit) => {
      const strategy = strategiesByUnit.get(String(unit.id));
      return strategy && Array.isArray(strategy.installments) && strategy.installments.length > 0;
    });
    const unitIds = eligibleUnits.map((unit) => unit.id);
    if (!projectBundle.units.length) return;
    if (!unitIds.length) {
      setError('قبل از ایجاد آگهی پیش فروش، باید الگوی فروش را همراه با مراحل پرداخت روی حداقل یک واحد اعمال کنید.');
      return;
    }
    const skippedCount = projectBundle.units.length - unitIds.length;
    await run(() => developersApi.createPresaleListings(selectedProject.id, {
      unit_ids: unitIds,
      lifecycle_status: 'DRAFT',
      availability_status: 'AVAILABLE'
    }), skippedCount > 0
      ? `آگهی های پیش فروش برای ${unitIds.length} واحد آماده ایجاد شدند. ${skippedCount} واحد به دلیل نداشتن شرایط فروش یا مراحل پرداخت رد شد.`
      : 'آگهی های پیش فروش به صورت پیش نویس ایجاد شدند.');
    await loadProjectBundle(selectedProject.id);
  };

  const saveSchedule = async (event) => {
    event.preventDefault();
    const activities = buildActivityPlan(projectBundle.activityCatalog, scheduleForm);
    if (!activities.length || activities.some((activity) => !activity.planned_start || !activity.planned_finish)) {
      setError('تاریخ شروع و پایان همه مراحل برنامه را ثبت کنید.');
      return;
    }

    const payload = {
      reason: scheduleForm.reason,
      effective_from: scheduleForm.effective_from || null,
      starting_activity: scheduleForm.starting_activity,
      activities
    };

    if (editingScheduleVersionId) {
      const updated = await run(
        () => developersApi.updateScheduleVersion(editingScheduleVersionId, payload),
        'تغییرات نسخه برنامه ذخیره شد.'
      );
      if (!updated) return;
    } else {
      const schedule = await run(() => developersApi.ensureConstructionSchedule(selectedProject.id));
      if (!schedule?.id) return;
      const created = await run(
        () => developersApi.createScheduleVersion({ ...payload, schedule: schedule.id }),
        'نسخه برنامه ساخت ایجاد شد.'
      );
      if (!created) return;
    }

    setScheduleForm(emptyScheduleForm);
    setEditingScheduleVersionId('');
    await loadProjectBundle(selectedProject.id);
  };

  const editSchedule = (version) => {
    const activities = version.activities || projectBundle.activities.filter(
      (activity) => String(activity.schedule_version) === String(version.id)
    );
    setEditingScheduleVersionId(version.id);
    setScheduleForm({
      reason: version.reason || '',
      effective_from: version.effective_from || '',
      starting_activity: version.starting_activity || activities[0]?.name || 'SITE_PREPARATION',
      section: activities[0]?.section || '',
      activities: activities.map((activity) => ({
        name: activity.name,
        planned_start: activity.planned_start,
        planned_finish: activity.planned_finish,
        section: activity.section || null,
        description: activity.description || ''
      }))
    });
  };

  const newSchedule = () => {
    setEditingScheduleVersionId('');
    setScheduleForm(emptyScheduleForm);
  };

  const deleteSchedule = async (versionId) => {
    const deleted = await run(
      () => developersApi.deleteScheduleVersion(versionId),
      'نسخه پیش نویس حذف شد.'
    );
    if (!deleted) return;
    if (String(editingScheduleVersionId) === String(versionId)) newSchedule();
    await loadProjectBundle(selectedProject.id);
  };

  const activateSchedule = async (versionId) => {
    const activated = await run(() => developersApi.activateScheduleVersion(versionId), 'برنامه ساخت فعال شد.');
    if (!activated) return;
    if (String(editingScheduleVersionId) === String(versionId)) newSchedule();
    await loadProjectBundle(selectedProject.id);
  };

  const nextProjectStep = async () => {
    setProjectNavBusy(true);
    try {
      if (projectStep === 1) {
        const saved = await savePlatformProject();
        if (!saved) return;
      }
      if (projectStep === 2) {
        const saved = await geoStepRef.current?.saveAnalysis();
        if (!saved) return;
      }
      setProjectStep((current) => Math.min(current + 1, projectWizardSteps.length));
    } finally {
      setProjectNavBusy(false);
    }
  };
  const backProjectStep = () => setProjectStep((current) => Math.max(current - 1, 1));

  return (
    <div className="dev-page" dir="rtl">
      <Header />
      <main className="dev-shell">
        <section className="dev-hero">
          <div>
            <p className="dev-kicker">مرکز کنترل سازنده</p>
            <h1>داشبورد پروژه‌های شما</h1>
            <p>وضعیت هر پروژه را یک‌جا ببینید، داده‌های آن را به‌روز کنید و برنامهٔ ساخت و فروش را پیش ببرید.</p>
          </div>
          <div className="dev-hero-metrics">
            <div><strong>{firms.length}</strong><span>سازنده</span></div>
            <div><strong>{platformProjects.length}</strong><span>پروژه پیش فروش</span></div>
            <div><strong>{portfolioProjects.length}</strong><span>پروژه سابقه</span></div>
          </div>
        </section>

        {error && <div className="dev-alert error">{error}</div>}
        {notice && <div className="dev-alert success">{notice}</div>}
        {selectedFirm && !canRegisterNewProject && !error && (
          <div className="dev-alert warning">
            {PROJECT_VERIFICATION_MESSAGE}{' '}
            <Link to="/profile">رفتن به پنل کاربری</Link>
          </div>
        )}

        <nav className="dev-tabs" aria-label="بخش های پنل سازندگان">
          {tabs.map((tab) => (
            <button key={tab.id} className={activeTab === tab.id ? 'active' : ''} onClick={() => openTab(tab.id)}>
              {tab.label}
            </button>
          ))}
        </nav>

        {activeTab === 'overview' && (
          <section className="dev-dashboard">
            <div className="dev-dashboard-toolbar">
              <div className="dev-dashboard-project-title">
                <span className="dev-dashboard-project-icon"><ApartmentRoundedIcon /></span>
                <div>
                  <small>پروژهٔ در حال مدیریت</small>
                  <strong>{selectedProject?.name || 'یک پروژه انتخاب کنید'}</strong>
                  {selectedProject?.address && <span>{selectedProject.address}</span>}
                </div>
              </div>
              <div className="dev-dashboard-switcher">
                <Select value={selectedProjectId} onChange={handleProjectSelect}>
                  <option value="">انتخاب پروژه</option>
                  {platformProjects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
                </Select>
                <button type="button" className="dev-btn secondary dev-icon-btn" onClick={startNewProject}>
                  <AddRoundedIcon /> پروژهٔ جدید
                </button>
              </div>
            </div>

            {!selectedProject ? (
              <div className="dev-panel dev-dashboard-empty">
                <span><ApartmentRoundedIcon /></span>
                <h2>اولین پروژه را به داشبورد اضافه کنید</h2>
                <p>پس از ثبت پروژه، می‌توانید اطلاعات، واحدها، فروش و زمان‌بندی ساخت را از همین نمای کلی مدیریت کنید.</p>
                <button type="button" className="dev-btn primary dev-icon-btn" onClick={startNewProject}><AddRoundedIcon /> ثبت پروژه</button>
              </div>
            ) : (
              <>
                <div className="dev-dashboard-kpis">
                  <div className="dev-dashboard-kpi">
                    <span className="dev-kpi-icon teal"><ApartmentRoundedIcon /></span>
                    <div><small>واحدهای پروژه</small><strong>{projectBundle.units.length}</strong><span>از {selectedProject.total_units || projectBundle.units.length || 0} واحد برنامه‌ریزی‌شده</span></div>
                  </div>
                  <div className="dev-dashboard-kpi">
                    <span className="dev-kpi-icon blue"><PaymentsRoundedIcon /></span>
                    <div><small>آمادهٔ پیش‌فروش</small><strong>{presaleReadyUnitCount}</strong><span>{projectBundle.strategies.length} شرایط فروش ثبت‌شده</span></div>
                  </div>
                  <div className="dev-dashboard-kpi">
                    <span className="dev-kpi-icon amber"><TimelineRoundedIcon /></span>
                    <div><small>پیشرفت ساخت</small><strong>{selectedProject.current_progress || 0}٪</strong><span>{projectBundle.activities.length} فعالیت ساخت</span></div>
                  </div>
                  <div className="dev-dashboard-kpi">
                    <span className="dev-kpi-icon green"><CheckCircleRoundedIcon /></span>
                    <div><small>آمادگی انتشار</small><strong>{dashboardProgress}٪</strong><span>{dashboardProgress === 100 ? 'آمادهٔ انتشار' : 'نیازمند تکمیل اطلاعات'}</span></div>
                  </div>
                </div>

                <div className="dev-dashboard-main">
                  <div className="dev-dashboard-content">
                    <div className="dev-panel dev-dashboard-status-card">
                      <div className="dev-panel-head">
                        <div>
                          <p className="dev-section-eyebrow">وضعیت امروز</p>
                          <h2>{dashboardProgress === 100 ? 'پروژه آمادهٔ انتشار است' : 'پروژه را برای انتشار آماده کنید'}</h2>
                          <p>{dashboardProgress === 100 ? 'اطلاعات اصلی پروژه کامل شده است.' : `تکمیل مرحلهٔ «${projectWizardSteps[nextIncompleteStep - 1]}» بهترین اقدام بعدی است.`}</p>
                        </div>
                        <Badge value={selectedProject.stage} tone={selectedProject.stage === 'COMPLETED' ? 'success' : 'warning'} />
                      </div>
                      <div className="dev-dashboard-progress" aria-label={`آمادگی پروژه ${dashboardProgress} درصد`}>
                        <span style={{ width: `${dashboardProgress}%` }} />
                      </div>
                      <div className="dev-dashboard-progress-meta"><span>{dashboardProgress}٪ تکمیل</span><span>{100 - dashboardProgress}٪ باقی‌مانده</span></div>
                      <button type="button" className="dev-btn primary dev-icon-btn" onClick={() => openProjectStep(nextIncompleteStep)}>
                        ادامهٔ تکمیل پروژه <ArrowBackRoundedIcon />
                      </button>
                    </div>

                    <ProjectVisualization
                      className="dev-dashboard-visualization"
                      sections={projectBundle.sections}
                      templates={projectBundle.templates}
                      units={projectBundle.units}
                      strategies={projectBundle.strategies}
                      defaultOverlay="status"
                    />

                    <div className="dev-panel dev-dashboard-plan-card">
                      <div className="dev-panel-head">
                        <div>
                          <p className="dev-section-eyebrow">برنامهٔ اجرایی</p>
                          <h2>زمان‌بندی ساخت</h2>
                          <p>{activeScheduleVersion ? `نسخهٔ ${activeScheduleVersion.number} برنامه ${statusLabels[activeScheduleVersion.status] || activeScheduleVersion.status}` : 'هنوز برنامه‌ای برای ساخت ثبت نشده است.'}</p>
                        </div>
                        <span className="dev-plan-icon"><CalendarMonthRoundedIcon /></span>
                      </div>
                      <div className="dev-plan-summary">
                        <div><small>نسخه‌های برنامه</small><strong>{projectBundle.schedules.length}</strong></div>
                        <div><small>فعالیت‌ها</small><strong>{projectBundle.activities.length}</strong></div>
                        <div><small>تاریخ تحویل</small><strong>{selectedProject.expected_completion_at || 'ثبت نشده'}</strong></div>
                      </div>
                      <button type="button" className="dev-btn secondary dev-icon-btn" onClick={() => openProjectStep(6)}>
                        <CalendarMonthRoundedIcon /> {activeScheduleVersion ? 'ویرایش برنامهٔ ساخت' : 'ساخت برنامهٔ اجرایی'}
                      </button>
                    </div>
                  </div>

                  <aside className="dev-dashboard-sidebar">
                    <div className="dev-panel">
                      <div className="dev-panel-head">
                        <div><p className="dev-section-eyebrow">دسترسی سریع</p><h2>ویرایش پروژه</h2></div>
                      </div>
                      <div className="dev-quick-actions">
                        <button type="button" onClick={() => openProjectStep(1)}><EditRoundedIcon /><span><strong>اطلاعات پروژه</strong><small>مشخصات، آدرس و تاریخ‌ها</small></span><ArrowBackRoundedIcon /></button>
                        <button type="button" onClick={() => openProjectStep(2)}><LocationOnRoundedIcon /><span><strong>تحلیل مکانی</strong><small>موقعیت و ویژگی‌های پیرامونی</small></span><ArrowBackRoundedIcon /></button>
                        <button type="button" onClick={() => openProjectStep(3)}><DescriptionRoundedIcon /><span><strong>مدارک و مجوزها</strong><small>{projectBundle.documents.length} مدرک بارگذاری‌شده</small></span><ArrowBackRoundedIcon /></button>
                        <button type="button" onClick={() => openProjectStep(4)}><ApartmentRoundedIcon /><span><strong>ساختار و واحدها</strong><small>{projectBundle.sections.length} بخش و {projectBundle.units.length} واحد</small></span><ArrowBackRoundedIcon /></button>
                        <button type="button" onClick={() => openProjectStep(5)}><PaymentsRoundedIcon /><span><strong>فروش و آگهی</strong><small>{projectBundle.salesTemplates.length} الگوی فروش</small></span><ArrowBackRoundedIcon /></button>
                        <button type="button" onClick={() => openProjectStep(6)}><CalendarMonthRoundedIcon /><span><strong>برنامهٔ ساخت</strong><small>{projectBundle.schedules.length} نسخهٔ برنامه</small></span><ArrowBackRoundedIcon /></button>
                      </div>
                    </div>

                    <div className="dev-panel dev-dashboard-checklist">
                      <div className="dev-panel-head">
                        <div><p className="dev-section-eyebrow">کنترل کیفیت داده</p><h2>چک‌لیست انتشار</h2></div>
                        <strong className="dev-checklist-score">{dashboardProgress}٪</strong>
                      </div>
                      <WizardChecklist status={wizardStatus} />
                    </div>

                    {selectedFirm && (
                      <div className="dev-panel dev-firm-compact">
                        <div><small>حساب سازنده</small><strong>{selectedFirm.display_name}</strong></div>
                        <Badge value={selectedFirm.verification_status} tone={selectedFirm.verification_status === 'VERIFIED' ? 'success' : 'warning'} />
                      </div>
                    )}
                  </aside>
                </div>
              </>
            )}
          </section>
        )}

        {activeTab === 'projects' && (
          <section className="dev-grid dev-project-grid">
            <div className="dev-panel wide dev-wizard-progress">
              <StepIndicator currentStep={projectStep} totalSteps={projectWizardSteps.length} labels={projectWizardSteps} />
            </div>
            {projectStep >= 4 && projectStep !== 5 && (
              <ProjectVisualization
                key={`project-visualization-step-${projectStep}`}
                className="wide dev-wizard-visualization"
                sections={projectBundle.sections}
                templates={projectBundle.templates}
                units={projectBundle.units}
                strategies={projectBundle.strategies}
                selectable={projectStep === 5}
                selectedUnitIds={selectedSalesUnitIds}
                onSelectionChange={setSelectedSalesUnitIds}
                defaultOverlay={projectStep === 5 ? 'sales' : (projectStep === 6 ? 'status' : 'structure')}
                pricePerMeter={salesForm.price}
              />
            )}
            {projectStep === 5 && (
              <div className="dev-wizard-sales-visual-row">
                <ProjectVisualization
                  key="project-visualization-step-5"
                  className="dev-wizard-visualization"
                  sections={projectBundle.sections}
                  templates={projectBundle.templates}
                  units={projectBundle.units}
                  strategies={projectBundle.strategies}
                  selectable
                  selectedUnitIds={selectedSalesUnitIds}
                  onSelectionChange={setSelectedSalesUnitIds}
                  defaultOverlay="sales"
                  pricePerMeter={salesForm.price}
                />
                <aside className="dev-panel dev-sales-visual-summary">
                  <div className="dev-panel-head">
                    <div>
                      <h2>الگوهای فروش و آگهی پیش فروش</h2>
                      <p>وضعیت فروش و آگهی برای واحدهای همین مدل قابل انتخاب است.</p>
                    </div>
                  </div>
                  <div className="dev-kpis dev-sales-visual-kpis">
                    <div><strong>{projectBundle.salesTemplates.length}</strong><span>الگوهای فروش</span></div>
                    <div><strong>{projectBundle.strategies.length}</strong><span>شرایط فروش واحدها</span></div>
                    <div><strong>{projectBundle.units.length}</strong><span>واحد قابل اعمال</span></div>
                    <div><strong>{wizardStatus?.steps?.presale_listings?.count || 0}</strong><span>آگهی های پیش فروش</span></div>
                  </div>
                </aside>
              </div>
            )}
            {projectStep === 1 && (
              <ProjectInfoStep
                busy={busy}
                platformProjects={platformProjects}
                projectForm={projectForm}
                selectedFirm={selectedFirm}
                selectedProject={selectedProject}
                selectedProjectId={selectedProjectId}
                canCreateProject={canRegisterNewProject}
                onProjectSelect={handleProjectSelect}
                onProjectFormChange={setProjectForm}
                onSave={savePlatformProject}
              />
            )}
            {projectStep === 2 && (
              <ProjectGeospatialStep
                ref={geoStepRef}
                busy={busy}
                projectForm={projectForm}
                projectId={selectedProjectId}
                selectedProject={selectedProject}
                onSaved={async () => {
                  const projectId = selectedProject?.id || selectedProjectId;
                  await loadFirmData(selectedFirm.id, projectId);
                  await loadProjectBundle(projectId);
                  setSelectedProjectId(projectId);
                }}
              />
            )}
            {projectStep === 3 && (
              <ProjectDocumentsStep
                busy={busy}
                selectedProject={selectedProject}
                documents={projectBundle.documents}
                onUpload={uploadProjectDocument}
              />
            )}
            {projectStep === 4 && (
              <ProjectStructureStep
                busy={busy}
                projectBundle={projectBundle}
                sectionForm={sectionForm}
                selectedProject={selectedProject}
                statusLabels={statusLabels}
                templateForm={templateForm}
                unitBatch={unitBatch}
                onCreateSection={createSection}
                onCreateTemplate={createTemplate}
                onDeleteSection={deleteSection}
                onDeleteTemplate={deleteUnitTemplate}
                onDeleteUnit={deleteUnit}
                onPublishRevision={publishRevision}
                onUploadPlan={uploadUnitTemplatePlan}
                onSectionFormChange={setSectionForm}
                onTemplateFormChange={setTemplateForm}
                onBulkCreateUnits={bulkCreateUnits}
                onUnitBatchChange={setUnitBatch}
              />
            )}
            {projectStep === 5 && (
              <ProjectSalesStep
                busy={busy}
                projectBundle={projectBundle}
                salesForm={salesForm}
                selectedProject={selectedProject}
                wizardStatus={wizardStatus}
                onApplySalesTemplate={applySalesTemplate}
                onDeleteSalesTemplate={deleteSalesTemplate}
                onDeleteUnitSalesStrategy={deleteUnitSalesStrategy}
                onCreateListings={createListings}
                onCreateSalesTemplate={createSalesTemplate}
                onSalesFormChange={setSalesForm}
                installments={salesInstallments}
                onInstallmentsChange={setSalesInstallments}
                selectedUnitIds={selectedSalesUnitIds}
                onSelectionChange={setSelectedSalesUnitIds}
                presaleReadyUnitCount={presaleReadyUnitCount}
                showSummary={false}
              />
            )}
            {projectStep === 6 && (
              <ProjectScheduleStep
                activeScheduleVersion={activeScheduleVersion}
                busy={busy}
                editingScheduleVersionId={editingScheduleVersionId}
                projectBundle={projectBundle}
                scheduleForm={scheduleForm}
                selectedProject={selectedProject}
                statusLabels={statusLabels}
                onActivateSchedule={activateSchedule}
                onDeleteSchedule={deleteSchedule}
                onEditSchedule={editSchedule}
                onNewSchedule={newSchedule}
                onSaveSchedule={saveSchedule}
                onScheduleFormChange={setScheduleForm}
              />
            )}
            <div className="dev-panel wide dev-wizard-nav">
              <button type="button" className="dev-btn secondary" onClick={backProjectStep} disabled={projectStep === 1}>مرحله قبل</button>
              {projectStep < projectWizardSteps.length && <button type="button" className="dev-btn primary" onClick={nextProjectStep} disabled={busy || projectNavBusy || (projectStep === 1 && !selectedFirm) || (projectStep !== 1 && !selectedProject)}>{projectNavBusy ? 'در حال ذخیره...' : 'مرحله بعد'}</button>}
              {projectStep === projectWizardSteps.length && <button type="button" className="dev-btn primary" onClick={() => setActiveTab('overview')} disabled={!selectedProject}>بازگشت به نمای کلی</button>}
            </div>
          </section>
        )}

      </main>
    </div>
  );
}
